import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ComicCard({ comic, variant = 'default' }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="block group flex-shrink-0 w-[160px] sm:w-[180px] cursor-pointer"
      >
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-secondary-100 mb-3">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩国漫画免费在线阅读`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute top-2 left-2">
            {comic.status === '连载中' && (
              <span className="bg-primary-500 text-background-50 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">连载</span>
            )}
            {comic.status === '已完结' && (
              <span className="bg-accent-500 text-background-50 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">完结</span>
            )}
          </div>
          {comic.newRelease && (
            <div className="absolute top-2 right-2">
              <span className="bg-red-500 text-background-50 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">NEW</span>
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">{comic.latestChapter}</p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${comic.id}`}
        className="block group cursor-pointer"
      >
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-secondary-100 mb-4">
          <img
            src={comic.coverImage}
            alt={comic.title}
            title={`${comic.title} - 韩国漫画免费在线阅读`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-base font-heading font-bold text-background-50 mb-1">{comic.title}</h3>
            <div className="flex items-center gap-3 text-xs text-background-50/80">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-primary-400 text-xs"></i>
                {comic.rating}
              </span>
              <span>{comic.chapterCount}话</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${comic.id}`}
      className="block group cursor-pointer"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-secondary-100 mb-4">
        <img
          src={comic.coverImage}
          alt={comic.title}
          title={`${comic.title} - 韩国漫画免费在线阅读`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          {comic.status === '连载中' && (
            <span className="bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded whitespace-nowrap">连载中</span>
          )}
          {comic.status === '已完结' && (
            <span className="bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded whitespace-nowrap">已完结</span>
          )}
        </div>
        {comic.newRelease && (
          <div className="absolute top-3 right-3">
            <span className="bg-red-500 text-background-50 text-xs px-2 py-0.5 rounded whitespace-nowrap">NEW</span>
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {comic.title}
      </h3>
      <div className="flex items-center gap-3 text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-star-fill text-primary-500 text-xs"></i>
          {comic.rating}
        </span>
        <span>{comic.author}</span>
      </div>
      <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.latestChapter}</p>
    </Link>
  );
}